import { useCallback, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Colors } from '~/libs/mui/theme'
import { useLogger } from '../useLogger'
import { useVideoScale } from '../useVideoScale'

type Props = {
  video: HTMLVideoElement
  currentTime: number
  duration: number
}

export const SeekBar = ({ video, currentTime, duration }: Props) => {
  const { videoScale } = useVideoScale()
  const normalizedVideoScale = useMemo(
    () => (isMobile ? videoScale * 1.5 : videoScale),
    [videoScale],
  )
  const [hover, setHover] = useState(false)
  const [hoverVideoTime, setHoverVideoTime] = useState(0)
  const [hoverX, setHoverX] = useState(0)
  const barRef = useRef<HTMLDivElement>(null)
  const { seek: seekLog } = useLogger()

  const barHeight = useMemo(
    () => (hover ? 1.5 * normalizedVideoScale * 4 : normalizedVideoScale * 4),
    [hover, normalizedVideoScale],
  )

  const markSize = useMemo(() => normalizedVideoScale * 20, [normalizedVideoScale])
  const markTranslateX = useMemo(() => {
    if (duration === 0) return -markSize / 2
    if (!barRef.current) {
      return 0
    }

    const scale = currentTime / duration
    return scale * barRef.current.offsetWidth - markSize / 2
  }, [currentTime, duration, markSize])

  const handleSeek = useCallback(
    (clientX: number) => {
      if (!barRef.current) return

      const rect = barRef.current.getBoundingClientRect()
      const clickX = clientX - rect.left
      const ratio = clickX / rect.width

      video.currentTime = ratio * duration
    },
    [duration, video],
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleSeek(e.clientX)
    },
    [handleSeek],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleSeek(e.clientX)

      const handleMouseMove = (e: MouseEvent) => {
        handleSeek(e.clientX)
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        seekLog()
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [handleSeek, seekLog],
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      handleSeek(e.touches[0].clientX)

      const handleTouchMove = (e: TouchEvent) => {
        handleSeek(e.touches[0].clientX)
      }

      const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
        seekLog()
      }

      document.addEventListener('touchmove', handleTouchMove)
      document.addEventListener('touchend', handleTouchEnd)
    },
    [handleSeek, seekLog],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!barRef.current) return
      const rect = barRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const ratio = Math.min(Math.max(x / rect.width, 0), 1)
      const time = ratio * duration

      setHoverVideoTime(time)
      setHoverX(x)
    },
    [duration],
  )

  return (
    <div
      style={{
        width: '100%',
        height: `${normalizedVideoScale * 40}px`,
        padding: `0 ${normalizedVideoScale * 32}px`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        ref={barRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          marginRight: `${normalizedVideoScale * 32}px`,
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onClick={handleClick}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            width: '100%',
            height: barHeight + 'px',
            backgroundColor: Colors.color7E7E80,
            transition: 'height 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transformOrigin: 'left',
            transform: `translateY(-50%) scaleX(${duration === 0 ? 0 : currentTime / duration})`,
            width: '100%',
            height: barHeight + 'px',
            backgroundColor: 'white',
            willChange: 'width',
            transition: 'height 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: `translateY(-50%) translateX(${markTranslateX}px)`,
            width: markSize,
            height: markSize,
            backgroundColor: 'white',
            borderRadius: '50%',
          }}
        />
        {hover && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: hoverX,
              transform: 'translateX(-50%)',
              color: 'white',
              fontSize: `${normalizedVideoScale * 20}px`,
              textShadow: '0 0 2px rgba(0, 0, 0, .5)',
            }}
          >
            {String(Math.floor(hoverVideoTime / 60)).padStart(2, '0')}:
            {String(Math.floor(hoverVideoTime % 60)).padStart(2, '0')}
          </div>
        )}
      </div>
      <div
        style={{
          color: 'white',
          fontSize: `${normalizedVideoScale * 20}px`,
          textShadow: '0 0 2px rgba(0, 0, 0, .5)',
        }}
      >
        {String(Math.floor(currentTime / 60)).padStart(2, '0')}:
        {String(Math.floor(currentTime % 60)).padStart(2, '0')}/
      </div>
      <div
        style={{
          color: Colors.colorAAA8A1,
          fontSize: `${normalizedVideoScale * 20}px`,
          textShadow: '0px 0px 1px rgba(0, 0, 0, 0.3)',
        }}
      >
        {String(Math.floor(duration / 60)).padStart(2, '0')}:
        {String(Math.floor(duration % 60)).padStart(2, '0')}
      </div>
    </div>
  )
}
